import { useEffect, useState, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useOnboardingStore } from '../../stores/onboarding.store';
import { useGiraffeTranslatorStore } from '../../stores/giraffe-translator.store';
import { useNeedsGuesserStore } from '../../stores/needs-guesser.store';
import { GIRAFFE_HINTS, NEEDS_GUESSER_HINTS } from '../../config/onboarding-steps';
import OnboardingSpeechBubble from './OnboardingSpeechBubble';
import { EMPY_LAUNCHER, shellBubbleAnchor } from './bubbleAnchor';
import SpotlightOverlay from './SpotlightOverlay';
import { useTourStepPresence } from '../../stores/tour-presence.store';
import { useIsDesktop } from '../../hooks/useMediaQuery';
import type {
  OnboardingTool,
  GiraffeHintStage,
  NeedsGuesserHintStage,
} from '../../types/onboarding';

interface GuidedToolHintsProps {
  tool: OnboardingTool;
  onContinueTour: () => void;
}

export default function GuidedToolHints({ tool, onContinueTour }: GuidedToolHintsProps) {
  if (tool === 'giraffe-translator') {
    return <GiraffeHints onContinueTour={onContinueTour} />;
  }
  return <NeedsGuesserHints onContinueTour={onContinueTour} />;
}

// ============================================
// Navbar Shield — dims & blocks the bottom nav during guided tool hints
// ============================================

function NavbarShield() {
  const isDesktop = useIsDesktop();
  // Mobile: navbar (~80px) + Empy button area (~160px total from bottom)
  // Desktop: just Empy + support chat (~80px from bottom)
  const height = isDesktop ? 80 : 160;

  return (
    <div
      className="fixed bottom-0 left-0 right-0 z-[201] bg-(--ee-scrim)/50 backdrop-blur-sm"
      style={{ height }}
    />
  );
}

// ============================================
// Thinking Dots — animated "." ".." "..." for loading states
// ============================================

function ThinkingDots() {
  const [dots, setDots] = useState(1);
  useEffect(() => {
    const interval = setInterval(() => {
      setDots((d) => (d >= 3 ? 1 : d + 1));
    }, 500);
    return () => clearInterval(interval);
  }, []);
  return (
    <p className="text-center text-lg font-bold tracking-widest text-(--ee-tw-gray-600) py-2">
      {'.'.repeat(dots)}
    </p>
  );
}

// ============================================
// Spotlight selector mapping per stage
// ============================================

const GT_SPOTLIGHT: Record<Exclude<GiraffeHintStage, 'done'>, string | null> = {
  'page-load': '[data-onboarding="giraffe-input"]',
  'input-ready': '[data-onboarding="giraffe-input"]',
  'analyze-highlight': '[data-onboarding="giraffe-analyze-btn"]',
  analyzing: null,
  'click-sentence': null, // no spotlight — bubble with count + Next
  'highlight-sentence': '[data-onboarding="giraffe-results"]', // spotlight only, no bubble
  finalize: '[data-onboarding="giraffe-finalize-btn"]',
  'card-shown': null,
};

const NG_SPOTLIGHT: Record<Exclude<NeedsGuesserHintStage, 'done'>, string | null> = {
  'page-load': '[data-onboarding="ng-input-scene"]',
  'input-ready': '[data-onboarding="ng-input-scene"]',
  'submit-highlight': '[data-onboarding="ng-submit-btn"]',
  refine: EMPY_LAUNCHER,
  'refine-select': '[data-onboarding="ng-refine-board"]',
  visualize: null, // no spotlight — just speech bubble, no overlay
};

// ============================================
// Giraffe Translator Hints
// ============================================

// ============================================
// Pulse helper — adds/removes .onboarding-pulse on elements by selector
// ============================================

/** Max polling attempts when waiting for lazy-loaded elements (100ms each → 3s total) */
const PULSE_MAX_POLL_ATTEMPTS = 30;
/** Initial delay before first poll attempt (ms) */
const PULSE_INITIAL_DELAY = 150;
/** Degrees the trace arc advances per animation frame (~2s per revolution at 60fps) */
const TRACE_SPEED_DEG_PER_FRAME = 3;

/** Elements that cannot accept element children (void elements + text-only content models) */
const NO_CHILD_ELEMENTS = new Set([
  'area',
  'base',
  'br',
  'col',
  'embed',
  'hr',
  'img',
  'input',
  'link',
  'meta',
  'param',
  'source',
  'track',
  'wbr',
  'textarea',
]);

/**
 * Creates a real DOM overlay div around a target element that renders an
 * animated conic-gradient ring. Unlike ::before pseudo-elements, real DOM
 * elements always repaint when their inline styles change — so the tracing
 * animation is reliable across all browsers.
 *
 * For elements that can't have children (void elements, textarea), returns
 * null and the caller falls back to a static CSS outline.
 */
function createTraceOverlay(el: Element): HTMLDivElement | null {
  if (NO_CHILD_ELEMENTS.has(el.tagName.toLowerCase())) return null;

  const overlay = document.createElement('div');
  overlay.className = 'onboarding-pulse-ring';

  // Shared styles — positioned absolutely around the target via reposition()
  Object.assign(overlay.style, {
    position: 'absolute',
    pointerEvents: 'none',
    zIndex: '-1',
    top: '-4px',
    left: '-4px',
    right: '-4px',
    bottom: '-4px',
    padding: '4px',
    borderRadius: 'inherit',
    // Mask: content-box XOR border-box = ring only
    WebkitMaskImage: 'linear-gradient(black 0 0), linear-gradient(black 0 0)',
    WebkitMaskClip: 'content-box, border-box',
    WebkitMaskComposite: 'xor',
    maskImage: 'linear-gradient(black 0 0), linear-gradient(black 0 0)',
    maskClip: 'content-box, border-box',
    maskComposite: 'exclude',
  } as Record<string, string>);

  el.appendChild(overlay);
  return overlay;
}

function setTraceAngle(overlay: HTMLDivElement, angle: number) {
  overlay.style.background = `conic-gradient(
    from ${angle}deg,
    transparent 0%,
    transparent 30%,
    rgba(230, 126, 34, 0.4) 50%,
    var(--ee-trace-glow) 65%,
    var(--ee-trace-core) 78%,
    var(--ee-trace-glow) 88%,
    transparent 100%
  )`;
}

function usePulse(selectors: string[]) {
  const [offScreenDirection, setOffScreenDirection] = useState<'up' | 'down' | null>(null);
  const firstSelector = selectors[0] ?? null;
  const rafRef = useRef<number>(0);

  useEffect(() => {
    if (selectors.length === 0) return;

    const overlays: HTMLDivElement[] = [];
    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    let pollId: ReturnType<typeof setTimeout>;
    let attempts = 0;

    // Poll until target elements appear in the DOM (they may load lazily)
    const tryAttach = () => {
      let found = false;
      selectors.forEach((sel) => {
        document.querySelectorAll(sel).forEach((el) => {
          if (!el.classList.contains('onboarding-pulse')) {
            el.classList.add('onboarding-pulse');
            const overlay = createTraceOverlay(el);
            if (overlay) {
              overlays.push(overlay);
              setTraceAngle(overlay, 0);
            } else {
              // Fallback: static outline for void/textarea elements
              el.classList.add('onboarding-pulse-outline');
            }
            found = true;
          }
        });
      });

      if (found && !prefersReducedMotion && overlays.length > 0 && !rafRef.current) {
        // Start the animation loop once overlays are attached
        let angle = 0;
        const animate = () => {
          angle = (angle + TRACE_SPEED_DEG_PER_FRAME) % 360;
          overlays.forEach((o) => setTraceAngle(o, angle));
          rafRef.current = requestAnimationFrame(animate);
        };
        rafRef.current = requestAnimationFrame(animate);
      }

      // Keep polling until max attempts — late-loading elements may still appear
      if (attempts < PULSE_MAX_POLL_ATTEMPTS) {
        attempts++;
        pollId = setTimeout(tryAttach, 100);
      }
    };

    // Start polling after a short initial delay
    pollId = setTimeout(tryAttach, PULSE_INITIAL_DELAY);

    return () => {
      clearTimeout(pollId);
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      rafRef.current = 0;
      overlays.forEach((o) => o.remove());
      selectors.forEach((sel) => {
        document.querySelectorAll(sel).forEach((el) => {
          el.classList.remove('onboarding-pulse');
          el.classList.remove('onboarding-pulse-outline');
        });
      });
    };
  }, [selectors.join(',')]); // eslint-disable-line react-hooks/exhaustive-deps

  // Track whether the first pulsed element is off-screen
  useEffect(() => {
    if (!firstSelector) {
      setOffScreenDirection(null);
      return;
    }

    const check = () => {
      const el = document.querySelector(firstSelector);
      if (!el) {
        setOffScreenDirection(null);
        return;
      }
      const rect = el.getBoundingClientRect();
      if (rect.bottom < 0) setOffScreenDirection('up');
      else if (rect.top > window.innerHeight) setOffScreenDirection('down');
      else setOffScreenDirection(null);
    };

    // Check immediately after pulse delay
    const timer = setTimeout(check, 200);
    window.addEventListener('scroll', check, { passive: true });
    window.addEventListener('resize', check, { passive: true });

    return () => {
      clearTimeout(timer);
      window.removeEventListener('scroll', check);
      window.removeEventListener('resize', check);
    };
  }, [firstSelector]);

  return {
    offScreenDirection,
    scrollToTarget: () => {
      if (!firstSelector) return;
      document
        .querySelector(firstSelector)
        ?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    },
  };
}

// ============================================
// Scroll Arrow — fixed indicator when pulsed element is off-screen
// ============================================

function PulseScrollArrow({
  direction,
  onClick,
}: {
  direction: 'up' | 'down';
  onClick: () => void;
}) {
  const isUp = direction === 'up';
  return (
    <button
      onClick={onClick}
      className="fixed left-1/2 -translate-x-1/2 z-[210] flex flex-col items-center gap-1 animate-bounce-slow"
      style={{ [isUp ? 'top' : 'bottom']: 24 }}
    >
      <div className="bg-primary text-(--ee-on-accent) rounded-full w-10 h-10 flex items-center justify-center shadow-lg">
        <i className={`fa-solid fa-chevron-${isUp ? 'up' : 'down'} text-sm`}></i>
      </div>
      <span className="text-xs text-primary font-medium bg-(--ee-surface)/90 px-2 py-0.5 rounded-full shadow-sm">
        Scroll {isUp ? 'up' : 'down'}
      </span>
    </button>
  );
}

/**
 * Determines which elements should pulse based on the current onboarding
 * stage and modal state. Returns an array of CSS selectors.
 */
function getGiraffePulseSelectors(
  stage: GiraffeHintStage | null,
  modalOpen: boolean,
  modalState: 'pitfall-details' | 'question' | 'success' | null,
  isTransforming: boolean,
): string[] {
  if (!stage) return [];

  // During highlight-sentence, pulse whatever the user needs to interact with
  if (stage === 'highlight-sentence') {
    if (!modalOpen) {
      return ['[data-onboarding="gt-sentence-pitfall"]'];
    }
    if (isTransforming) return []; // transforming overlay shown, no pulse
    switch (modalState) {
      case 'pitfall-details':
        return ['[data-onboarding="gt-begin-transform"]'];
      // Every round of the conversation, follow-ups included: the reply box
      // and Submit (there is no separate "Try Again" step any more).
      case 'question':
        return ['[data-onboarding="gt-answer-input"]', '[data-onboarding="gt-submit-answer"]'];
      case 'success':
        return ['[data-onboarding="gt-update-sentence"]'];
      default:
        return [];
    }
  }

  if (stage === 'finalize') {
    return ['[data-onboarding="giraffe-finalize-btn"]'];
  }

  if (stage === 'card-shown') {
    return ['[data-onboarding="gt-share-btn"]'];
  }

  return [];
}

/** Where the transform dialog lives (app.router.tsx). */
const GIRAFFE_TOOL_ROUTE = '/app/tools/giraffe-translator';

function GiraffeHints({ onContinueTour }: { onContinueTour: () => void }) {
  const toolHintStage = useOnboardingStore((s) => s.toolHintStage) as GiraffeHintStage | null;
  const setToolHintStage = useOnboardingStore((s) => s.setToolHintStage);
  const modal = useGiraffeTranslatorStore((s) => s.modal);
  const { pathname } = useLocation();
  // Every stage is a tour step, the pulse-only ones (highlight-sentence,
  // finalize: no bubble, the pulsing target IS the step) included. In the
  // transform dialog the pulsing controls are in its bottom bar (Begin
  // Transformation, the reply box and Submit, Update Sentence), so an alert
  // goes to the top there; on the page, to the bottom.
  //
  // In the dialog this component draws nothing itself: the step is the pulse
  // on the dialog's controls, and the dialog exists only on the tool page.
  // Back from it leaves the dialog open in the store, and on any other page
  // nothing of the tour is on screen, so nothing is held back there.
  const inDialog = modal.isOpen && modal.sentenceIndex !== null;
  const onToolPage = pathname.startsWith(GIRAFFE_TOOL_ROUTE);
  useTourStepPresence(
    toolHintStage !== null && toolHintStage !== 'done' && (!inDialog || onToolPage),
    modal.isOpen ? 'top' : 'bottom',
  );

  // Giraffe translator store state
  const inputText = useGiraffeTranslatorStore((s) => s.inputText);
  const analysisResults = useGiraffeTranslatorStore((s) => s.analysisResults);
  const isAnalyzing = useGiraffeTranslatorStore((s) => s.isAnalyzing);
  const isTransforming = useGiraffeTranslatorStore((s) => s.isTransforming);
  const transformedSentences = useGiraffeTranslatorStore((s) => s.transformedSentences);
  const removedSentences = useGiraffeTranslatorStore((s) => s.removedSentences);
  const showTransformationCard = useGiraffeTranslatorStore((s) => s.showTransformationCard);
  const areAllSentencesTransformed = useGiraffeTranslatorStore((s) => s.areAllSentencesTransformed);

  // Track modal open/close transitions to detect when user finishes a sentence
  const prevModalOpen = useRef(false);

  // === Pulsing border management ===
  const pulseSelectors = getGiraffePulseSelectors(
    toolHintStage,
    modal.isOpen,
    modal.currentState,
    isTransforming,
  );
  const { offScreenDirection, scrollToTarget } = usePulse(pulseSelectors);

  // === Scroll management ===

  // Scroll to top when first entering the giraffe translator page
  useEffect(() => {
    if (toolHintStage === 'page-load') {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Scroll results into view when entering highlight-sentence with no modal
  useEffect(() => {
    if (toolHintStage === 'highlight-sentence' && !modal.isOpen) {
      const id = setTimeout(() => {
        document
          .querySelector('[data-onboarding="giraffe-results"]')
          ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 100);
      return () => clearTimeout(id);
    }
  }, [toolHintStage, modal.isOpen]);

  // Scroll finalize button into view
  useEffect(() => {
    if (toolHintStage === 'finalize') {
      const id = setTimeout(() => {
        document
          .querySelector('[data-onboarding="giraffe-finalize-btn"]')
          ?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 100);
      return () => clearTimeout(id);
    }
  }, [toolHintStage]);

  // === Stage transitions ===

  // page-load → input-ready: user typed > 5 chars
  useEffect(() => {
    if (toolHintStage === 'page-load' && inputText.trim().length > 5) {
      setToolHintStage('input-ready');
    }
  }, [inputText, toolHintStage, setToolHintStage]);

  // Any early stage → analyzing: isAnalyzing becomes true
  useEffect(() => {
    if (
      isAnalyzing &&
      (toolHintStage === 'page-load' ||
        toolHintStage === 'input-ready' ||
        toolHintStage === 'analyze-highlight')
    ) {
      setToolHintStage('analyzing');
    }
  }, [isAnalyzing, toolHintStage, setToolHintStage]);

  // Any pre-results stage → click-sentence (or done if no pitfalls)
  useEffect(() => {
    if (
      analysisResults &&
      analysisResults.length > 0 &&
      (toolHintStage === 'page-load' ||
        toolHintStage === 'input-ready' ||
        toolHintStage === 'analyze-highlight' ||
        toolHintStage === 'analyzing')
    ) {
      const hasPitfalls = analysisResults.some((s) => s.pitfalls.length > 0);
      setToolHintStage(hasPitfalls ? 'click-sentence' : 'done');
    }
  }, [analysisResults, toolHintStage, setToolHintStage]);

  // highlight-sentence: detect modal close → check if all sentences handled
  useEffect(() => {
    const wasOpen = prevModalOpen.current;
    prevModalOpen.current = modal.isOpen;

    if (toolHintStage === 'highlight-sentence' && wasOpen && !modal.isOpen) {
      if (areAllSentencesTransformed()) {
        const remaining = analysisResults?.filter((_, i) => !removedSentences.has(i)).length ?? 0;
        setToolHintStage(remaining === 0 ? 'done' : 'finalize');
      } else {
        // More sentences to handle — show count message again
        setToolHintStage('click-sentence');
      }
    }
  }, [
    modal.isOpen,
    toolHintStage,
    setToolHintStage,
    areAllSentencesTransformed,
    analysisResults,
    removedSentences,
  ]);

  // finalize → card-shown: transformation card appears
  useEffect(() => {
    if (toolHintStage === 'finalize' && showTransformationCard) {
      setToolHintStage('card-shown');
    }
  }, [showTransformationCard, toolHintStage, setToolHintStage]);

  // done → auto-advance to quick tour
  useEffect(() => {
    if (toolHintStage === 'done') {
      onContinueTour();
    }
  }, [toolHintStage, onContinueTour]);

  const stage = toolHintStage as GiraffeHintStage | null;
  if (!stage || stage === 'done') return null;

  const isModalOpen = modal.isOpen && modal.sentenceIndex !== null;
  const isThinking = stage === 'analyzing';
  // Hide bubble during highlight-sentence (pulse guides the user) and finalize (pulse on button)
  // card-shown shows a bubble with "Continue Tour" alongside the share pulse
  const isBubbleHidden = stage === 'highlight-sentence' || stage === 'finalize';
  const spotlightSelector = GT_SPOTLIGHT[stage];

  // Dynamic text for click-sentence stage
  const getClickSentenceText = () => {
    if (!analysisResults) return '';
    const remaining = analysisResults.filter(
      (s, i) => s.pitfalls.length > 0 && !removedSentences.has(i),
    ).length;

    if (remaining === 0) return '';

    const pitfallCount = analysisResults
      .filter((s, i) => s.pitfalls.length > 0 && !removedSentences.has(i))
      .reduce((sum, s) => sum + s.pitfalls.length, 0);

    const hasHandled = Object.keys(transformedSentences).length > 0 || removedSentences.size > 0;

    if (!hasHandled) {
      return `I found ${pitfallCount} pitfall${pitfallCount !== 1 ? 's' : ''} in ${remaining} sentence${remaining !== 1 ? 's' : ''}! Follow the pulsing border to transform each one.`;
    }
    return `${remaining} sentence${remaining !== 1 ? 's' : ''} left to go! Follow the pulsing border.`;
  };

  const hintText = stage === 'click-sentence' ? getClickSentenceText() : GIRAFFE_HINTS[stage];

  return (
    <>
      {/* Navbar shield — dims & blocks bottom nav during guided hints */}
      {!isModalOpen && <NavbarShield />}

      {/* Spotlight — show during pre-modal stages only (pulse handles modal guidance) */}
      {!isModalOpen && !isThinking && spotlightSelector ? (
        <SpotlightOverlay targetSelector={spotlightSelector} />
      ) : null}

      {/* Scroll arrow — when the pulsed element is off-screen */}
      {offScreenDirection ? (
        <PulseScrollArrow direction={offScreenDirection} onClick={scrollToTarget} />
      ) : null}

      {/* Speech bubble — hide during modal, pulsed stages, and analyzing dots */}
      {!isModalOpen && !isBubbleHidden && (
        <OnboardingSpeechBubble visible maxWidth={300}>
          {isThinking ? (
            <ThinkingDots />
          ) : stage === 'card-shown' ? (
            <>
              <p className="text-sm text-(--ee-tw-gray-800) leading-relaxed mb-3">
                Here&apos;s your transformed message! Share it with someone you care about, then
                continue when you&apos;re ready.
              </p>
              <div className="flex gap-2">
                <button
                  onClick={onContinueTour}
                  className="flex-1 py-2.5 rounded-xl bg-primary text-(--ee-on-accent) text-sm font-medium hover:bg-primary/90 transition-colors"
                >
                  Continue Tour
                </button>
              </div>
            </>
          ) : (
            <>
              {hintText ? (
                <p className="text-sm text-(--ee-tw-gray-800) leading-relaxed mb-3">{hintText}</p>
              ) : null}
              <div className="flex gap-2">
                <button
                  onClick={onContinueTour}
                  className="flex-1 py-2 rounded-xl text-sm text-(--ee-tw-gray-500) hover:bg-(--ee-tw-gray-100-bg) transition-colors"
                >
                  Skip
                </button>
                {stage === 'input-ready' && (
                  <button
                    onClick={() => setToolHintStage('analyze-highlight')}
                    className="flex-1 py-2 rounded-xl bg-primary text-(--ee-on-accent) text-sm font-medium hover:bg-primary/90 transition-colors"
                  >
                    Next
                  </button>
                )}
                {stage === 'click-sentence' && (
                  <button
                    onClick={() => setToolHintStage('highlight-sentence')}
                    className="flex-1 py-2 rounded-xl bg-primary text-(--ee-on-accent) text-sm font-medium hover:bg-primary/90 transition-colors"
                  >
                    Next
                  </button>
                )}
              </div>
            </>
          )}
        </OnboardingSpeechBubble>
      )}
    </>
  );
}

// ============================================
// Needs Guesser Hints
// ============================================

function NeedsGuesserHints({ onContinueTour }: { onContinueTour: () => void }) {
  const isDesktop = useIsDesktop();
  const toolHintStage = useOnboardingStore((s) => s.toolHintStage) as NeedsGuesserHintStage | null;
  // Every stage is a tour step, refine-select (spotlight only) included.
  useTourStepPresence(toolHintStage !== null && toolHintStage !== 'done');
  const setToolHintStage = useOnboardingStore((s) => s.setToolHintStage);

  const currentScreen = useNeedsGuesserStore((s) => s.currentScreen);
  const inputSentence = useNeedsGuesserStore((s) => s.inputSentence);

  // Advance to input-ready as soon as >15 chars typed
  useEffect(() => {
    if (toolHintStage === 'page-load' && inputSentence.trim().length > 15) {
      setToolHintStage('input-ready');
    }
  }, [inputSentence, toolHintStage, setToolHintStage]);

  // Watch screen transitions
  useEffect(() => {
    if (
      currentScreen === 'refine' &&
      (toolHintStage === 'page-load' ||
        toolHintStage === 'input-ready' ||
        toolHintStage === 'submit-highlight')
    ) {
      setToolHintStage('refine');
    }
  }, [currentScreen, toolHintStage, setToolHintStage]);

  useEffect(() => {
    if (
      currentScreen === 'visualize' &&
      (toolHintStage === 'page-load' ||
        toolHintStage === 'input-ready' ||
        toolHintStage === 'submit-highlight' ||
        toolHintStage === 'refine' ||
        toolHintStage === 'refine-select')
    ) {
      setToolHintStage('visualize');
    }
  }, [currentScreen, toolHintStage, setToolHintStage]);

  // done → auto-advance to quick tour
  useEffect(() => {
    if (toolHintStage === 'done') {
      onContinueTour();
    }
  }, [toolHintStage, onContinueTour]);

  const stage = toolHintStage as NeedsGuesserHintStage | null;
  if (!stage || stage === 'done') return null;

  // Don't show lockout during processing screen — it has its own loading modal
  const isProcessing = currentScreen === 'processing';

  const hintText = NEEDS_GUESSER_HINTS[stage];
  const spotlightSelector = NG_SPOTLIGHT[stage];

  // refine-select: spotlight only, no speech bubble
  const showBubble = stage !== 'refine-select';
  // refine spotlights Empy, docked in the compact header below 1280: the
  // bubble points at it there (./bubbleAnchor); every other stage's target
  // is on the page and keeps the default place.
  const bubbleAnchor = stage === 'refine' ? shellBubbleAnchor(EMPY_LAUNCHER, isDesktop) : undefined;

  return (
    <>
      {/* Navbar shield — dims & blocks bottom nav during guided hints */}
      {!isProcessing && stage !== 'refine' && <NavbarShield />}

      {/* Spotlight lockout — only when NOT processing AND we have a target selector */}
      {!isProcessing && spotlightSelector ? (
        <SpotlightOverlay
          targetSelector={spotlightSelector}
          borderRadius={stage === 'refine' ? 9999 : undefined}
          padding={stage === 'refine' ? 4 : undefined}
        />
      ) : null}

      {!isProcessing && showBubble ? (
        <OnboardingSpeechBubble
          visible
          maxWidth={300}
          anchor={bubbleAnchor}
          target={stage === 'refine' ? EMPY_LAUNCHER : undefined}
        >
          {hintText ? (
            <p className="text-sm text-(--ee-tw-gray-800) leading-relaxed mb-3">{hintText}</p>
          ) : null}

          <div className="flex gap-2">
            <button
              onClick={onContinueTour}
              className="flex-1 py-2 rounded-xl text-sm text-(--ee-tw-gray-500) hover:bg-(--ee-tw-gray-100-bg) transition-colors"
            >
              Skip
            </button>
            {stage === 'input-ready' && (
              <button
                onClick={() => setToolHintStage('submit-highlight')}
                className="flex-1 py-2 rounded-xl bg-primary text-(--ee-on-accent) text-sm font-medium hover:bg-primary/90 transition-colors"
              >
                Next
              </button>
            )}
            {stage === 'refine' && (
              <button
                onClick={() => setToolHintStage('refine-select')}
                className="flex-1 py-2 rounded-xl bg-primary text-(--ee-on-accent) text-sm font-medium hover:bg-primary/90 transition-colors"
              >
                OK
              </button>
            )}
            {stage === 'visualize' && (
              <button
                onClick={onContinueTour}
                className="flex-1 py-2 rounded-xl bg-primary text-(--ee-on-accent) text-sm font-medium hover:bg-primary/90 transition-colors"
              >
                Next
              </button>
            )}
          </div>
        </OnboardingSpeechBubble>
      ) : null}
    </>
  );
}
